import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { applyDocumentLanguage } from '@/i18n';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Globe, Check } from 'lucide-react';

// Compact pill button that opens a language-picker dialog.
// Used on every pre-login screen (splash, onboarding, login, register,
// verify OTP) so users can switch language without having to sign up
// first. Applies the language immediately on card tap — no separate
// Save action — and closes the dialog.
//
// Props:
//   variant   "light" (default, dark text on light background — for
//              white-card contexts like login/register) or "dark"
//              (light text on translucent white — for the green
//              splash screen)
const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'ha', name: 'Hausa', native: 'Hausa' },
  { code: 'yo', name: 'Yoruba', native: 'Yorùbá' },
  { code: 'ig', name: 'Igbo', native: 'Igbo' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
];

export default function LanguagePickerButton({ variant = 'light', size = 'sm' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const handleSelect = async (code) => {
    await i18n.changeLanguage(code);
    applyDocumentLanguage(code);
    setOpen(false);
  };

  const chipClass = variant === 'dark'
    ? 'bg-white/15 text-white hover:bg-white/25 border-white/30'
    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {size === 'xl' ? (
        // The first screen's version (beta feedback): impossible to miss,
        // so farmers pick their language before anything else.
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full min-h-[4.5rem] rounded-2xl bg-white text-farm-forest shadow-lg border-4 border-farm-gold-light inline-flex items-center justify-center gap-3 px-4 py-3 active:scale-[0.99]"
          data-testid="language-picker-btn"
        >
          <Globe className="w-9 h-9 text-farm-primary flex-shrink-0" />
          <span className="text-left leading-tight">
            <span className="block text-2xl font-extrabold">{current.native}</span>
            <span className="block text-sm font-semibold text-farm-forest/70">{t('languageSelect.tapToChange')}</span>
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${chipClass}`}
          data-testid="language-picker-btn"
        >
          <Globe className="w-4 h-4" />
          <span>{current.native}</span>
        </button>
      )}

      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-center">{t('languageSelect.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                i18n.language === lang.code
                  ? 'border-farm-primary bg-farm-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <Globe className="w-4 h-4 text-gray-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">{lang.native}</p>
                  <p className="text-xs text-gray-500">{lang.name}</p>
                </div>
              </div>
              {i18n.language === lang.code && (
                <div className="w-5 h-5 bg-farm-primary rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
